import { Component, ContentChildren, QueryList, Input, Output, EventEmitter, AfterContentInit, signal, inject, OnInit, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToggleButtonOptionDirective } from './toggle-button-option.directive';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-toggle-button',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="toggle-button-container">
      <div class="toggle-button-wrapper">
        @if (selectedValue() && selectedIndex() >= 0) {
          <div 
            class="toggle-indicator"
            [style.transform]="'translateX(' + getIndicatorPosition() + 'px)'"
            [style.width.px]="getIndicatorWidth()"
          ></div>
        }
        @for (option of options; track option.appToggleOption; let i = $index) {
          <button
            class="toggle-button-item"
            [class.active]="selectedValue() === option.appToggleOption"
            [attr.data-index]="i"
            (click)="selectOption(option.appToggleOption, i, true)"
            #buttonEl
          >
            <div class="icon-wrapper">
              <ng-container *ngTemplateOutlet="option.templateRef"></ng-container>
            </div>
            @if (selectedValue() === option.appToggleOption) {
              <span class="label">
                {{ option.label }}
              </span>
            }
          </button>
        }
      </div>
    </div>
  `,
  styleUrls: ['./toggle-button.component.scss']
})
export class ToggleButtonComponent implements AfterContentInit, OnInit {
  @ContentChildren(ToggleButtonOptionDirective) options!: QueryList<ToggleButtonOptionDirective>;
  private readonly router = inject(Router);
  private readonly elementRef = inject(ElementRef);
  
  selectedValue = signal<string | null>(null);
  selectedIndex = signal<number>(-1);
  indicatorWidth = signal<number>(0);
  indicatorPosition = signal<number>(0);
  isFirstSelection = true;
  
  @Input() 
  set value(val: string | null) {
    this.selectedValue.set(val);
  }
  
  get value(): string | null {
    return this.selectedValue();
  }
  
  @Input() paths: Record<string, string> = {};
  @Input() navigationCallbacks: Record<string, () => void> = {};

  @Output() selectionChange = new EventEmitter<string>();

  ngOnInit() {
    this.checkUrlAndSelectOption();
    
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.checkUrlAndSelectOption();
    });
  }

  ngAfterContentInit() {
    setTimeout(() => this.checkUrlAndSelectOption(), 0);
  }

  private checkUrlAndSelectOption() {
    const currentUrl = this.router.url.split('?')[0];
    
    let matchFound = false;
    
    for (const [key, path] of Object.entries(this.paths)) {
      if (this.matchPath(currentUrl, path)) {
        const optionIndex = this.options?.toArray().findIndex(opt => opt.appToggleOption === key);
        if (optionIndex !== undefined && optionIndex >= 0) {
          this.selectOption(key, optionIndex, false);
          matchFound = true;
        }
        break;
      }
    }
    
    if (!matchFound) {
      this.clearSelection();
    }
  }

  private matchPath(currentUrl: string, path: string): boolean {
    const pathSegments = path.split('/');
    const urlSegments = currentUrl.split('/');
    
    if (pathSegments[1] === urlSegments[1]) {
      return true;
    }
    
    return false;
  }

  selectOption(value: string, index: number, shouldNavigate: boolean = true) {
    if (this.selectedValue() !== value) {
      if (this.isFirstSelection && this.selectedIndex() === -1) {
        this.indicatorPosition.set(0);
        this.indicatorWidth.set(0);
        this.isFirstSelection = false;
        
        setTimeout(() => {
          this.selectedValue.set(value);
          this.selectedIndex.set(index);
          this.updateIndicator(index);
        }, 10);
      } else {
        this.selectedValue.set(value);
        this.selectedIndex.set(index);
        this.updateIndicator(index);
      }
      
      if (shouldNavigate) {
        this.selectionChange.emit(value);

        if (this.navigationCallbacks[value]) {
          this.navigationCallbacks[value]();
        } else if (this.paths[value]) {
          this.router.navigate([this.paths[value]], { queryParamsHandling: 'preserve' });
        }
      }
    }
  }
  
  clearSelection() {
    this.selectedValue.set(null);
    this.selectedIndex.set(-1);
    this.indicatorWidth.set(0);
    this.indicatorPosition.set(0);
    this.isFirstSelection = true;
  }

  updateIndicator(index: number) {
    this.calculateIndicatorPosition(index);
    
    setTimeout(() => {
      this.calculateIndicatorPosition(index);
    }, 300);
  }
  
  private calculateIndicatorPosition(index: number) {
    const componentElement = this.elementRef.nativeElement;
    const buttons = componentElement.querySelectorAll('.toggle-button-item');
    const wrapper = componentElement.querySelector('.toggle-button-wrapper');
    
    if (buttons[index] && wrapper) {
      const button = buttons[index] as HTMLElement;
      const wrapperRect = wrapper.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();
      this.indicatorPosition.set(buttonRect.left - wrapperRect.left);
      this.indicatorWidth.set(buttonRect.width);
    }
  }

  getIndicatorPosition(): number {
    return this.indicatorPosition();
  }

  getIndicatorWidth(): number {
    return this.indicatorWidth();
  }
}